import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  map,
  startWith,
} from 'rxjs';
import { VendorNavGroup } from '../models/company-permission.model';
import { ContextService } from './context.service';
import { getVendorNavGroup } from '../utils/company-permission.util';
import {
  getVendorListTabs,
  shouldShowVendorListTabs,
  sortVendorListTabsForDesign,
} from '../utils/vendor-list-tab.util';

type VendorPermissionSnapshot = {
  roleId: string | null;
  companyId: string | null;
  navGroup: VendorNavGroup | null;
};

@Injectable({
  providedIn: 'root',
})
export class VendorPermissionService {
  private readonly snapshot$ = new BehaviorSubject<VendorPermissionSnapshot>(
    this.buildSnapshot()
  );

  readonly navGroup$ = this.snapshot$.pipe(map((s) => s.navGroup));

  constructor(private readonly contextSvc: ContextService) {
    combineLatest([
      this.contextSvc.selectedRoleId$.pipe(
        startWith(this.contextSvc.selectedRoleId$.value)
      ),
      this.contextSvc.selectedCompanyId$.pipe(
        startWith(this.contextSvc.getCompanyId())
      ),
      this.contextSvc.user.pipe(startWith(this.contextSvc.user.value)),
      this.contextSvc.onApRolePermissionsChanged$.pipe(startWith(null)),
    ])
      .pipe(
        map(() => this.buildSnapshot()),
        distinctUntilChanged(
          (a, b) =>
            a.roleId === b.roleId &&
            a.companyId === b.companyId &&
            JSON.stringify(a.navGroup) === JSON.stringify(b.navGroup)
        )
      )
      .subscribe((snapshot) => this.snapshot$.next(snapshot));
  }

  getNavGroup(): VendorNavGroup | null {
    return this.snapshot$.value.navGroup;
  }

  getVendorListTabs() {
    const snapshot = this.snapshot$.value;
    return sortVendorListTabsForDesign(
      getVendorListTabs(
        this.contextSvc.getStoredPermissionsForRole(snapshot.roleId),
        snapshot.companyId,
        snapshot.roleId
      )
    );
  }

  shouldShowListTabs(): boolean {
    const snapshot = this.snapshot$.value;
    return shouldShowVendorListTabs(
      this.getVendorListTabs(),
      snapshot.roleId
    );
  }

  hasSidebar(): boolean {
    return Boolean(this.getNavGroup()?.subMenus?.length);
  }

  private buildSnapshot(): VendorPermissionSnapshot {
    const roleId = this.contextSvc.selectedRoleId$.value;
    const companyId = this.contextSvc.getCompanyId();

    return {
      roleId,
      companyId,
      navGroup: getVendorNavGroup(
        this.contextSvc.getStoredPermissionsForRole(roleId),
        companyId,
        roleId
      ),
    };
  }
}
